import { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldOff, KeyRound, Copy, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { MfaRequirements } from '../../lib/mfaGate';
import MfaEnrollmentPanel from './MfaEnrollmentPanel';

interface FactorInfo {
  id: string;
  status: string;
  created_at?: string;
}

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? '';
}

// Account Settings → Security: voluntary enrollment, factor status,
// unenrollment, and backup-code regeneration. The forced-enrollment path
// lives in MfaEnrollmentScreen; this tab is the self-service surface.
export function SecurityTab() {
  const [loading, setLoading] = useState(true);
  const [factor, setFactor] = useState<FactorInfo | null>(null);
  const [requirements, setRequirements] = useState<MfaRequirements | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [freshCodes, setFreshCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: factors, error: listError }, { data: req, error: reqError }] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.rpc('get_mfa_requirements'),
      ]);
      if (listError) throw listError;
      if (reqError) throw new Error(reqError.message);
      const verified = factors?.totp?.find(f => f.status === 'verified') ?? null;
      setFactor(verified ? { id: verified.id, status: verified.status, created_at: verified.created_at } : null);
      setRequirements((req as MfaRequirements) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load security settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const handleEnrollComplete = () => {
    setEnrolling(false);
    setNotice('Two-factor authentication is on.');
    void loadStatus();
  };

  const handleUnenroll = async () => {
    if (!factor) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (unenrollError) throw unenrollError;
      setNotice('Two-factor authentication is off.');
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error
        ? err.message
        : 'Could not remove the authenticator. Sign out and back in (verifying with your code), then try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    setFreshCodes([]);
    try {
      const token = await getToken();
      const res = await fetch('/.netlify/functions/mfa-backup-codes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'regenerate' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate backup codes.');
      setFreshCodes(data.codes || []);
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate backup codes.');
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(freshCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — codes stay visible.
    }
  };

  if (loading) {
    return <div className="text-center py-10 text-gray-500 text-sm">Loading security settings…</div>;
  }

  const mfaRequired = requirements?.enrollment_required || (factor !== null && requirements?.firm_policy !== null && requirements?.firm_policy !== 'off');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            {factor ? <ShieldCheck className="w-6 h-6 text-blue-600" /> : <ShieldOff className="w-6 h-6 text-blue-600" />}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-600">
              {factor ? 'On — an authenticator app protects this account.' : 'Off — protect your account with an authenticator app.'}
            </p>
          </div>
        </div>

        {notice && <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{notice}</p>}
        {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="security-error">{error}</p>}

        {!factor && !enrolling && (
          <button
            onClick={() => setEnrolling(true)}
            data-testid="security-enable-mfa"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            Enable two-factor authentication
          </button>
        )}

        {!factor && enrolling && <MfaEnrollmentPanel onComplete={handleEnrollComplete} />}

        {factor && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enrolled{factor.created_at ? ` on ${new Date(factor.created_at).toLocaleDateString()}` : ''}. You'll be asked for a code when you sign in.
            </p>
            {requirements?.enrollment_required === false && requirements?.firm_policy && requirements.firm_policy !== 'off' ? (
              <p className="text-xs text-gray-500">Your firm requires two-factor authentication, so it cannot be turned off here.</p>
            ) : (
              <button
                onClick={handleUnenroll}
                disabled={busy || mfaRequired === true}
                data-testid="security-disable-mfa"
                className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {busy ? 'Working…' : 'Turn off two-factor authentication'}
              </button>
            )}
          </div>
        )}
      </div>

      {factor && requirements?.backup_codes_allowed !== false && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <KeyRound className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Backup Codes</h3>
              <p className="text-sm text-gray-600">
                {requirements?.remaining_backup_codes ?? 0} unused code{(requirements?.remaining_backup_codes ?? 0) === 1 ? '' : 's'} remaining.
                Each works once and removes your authenticator so you can enroll a new one.
              </p>
            </div>
          </div>

          {freshCodes.length > 0 && (
            <div className="mb-4 space-y-3">
              <p className="text-sm text-gray-600">Save these now — they replace all previous codes and are only shown once.</p>
              <div data-testid="security-backup-codes" className="grid grid-cols-2 gap-2 bg-gray-50 border border-gray-200 rounded-md p-4 font-mono text-sm">
                {freshCodes.map(c => <span key={c}>{c}</span>)}
              </div>
              <button onClick={copyCodes} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy codes'}
              </button>
            </div>
          )}

          <button
            onClick={handleRegenerate}
            disabled={busy}
            data-testid="security-regenerate-codes"
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {busy ? 'Working…' : 'Generate new backup codes'}
          </button>
          <p className="mt-2 text-xs text-gray-400">Generating new codes invalidates every previous code.</p>
        </div>
      )}
    </div>
  );
}
